import { useCallback, useEffect, useMemo, useState } from "react"
import { useMenuData } from "../offline/menuData"
import { SYNC_PHASE } from "../offline/syncEngine"
import {
  ALLERGEN_FILTERS,
  ITEM_KIND,
  MEAL_PERIOD,
  normalizeBarItem,
  normalizeFoodItem,
  normalizeSpiritItem,
  normalizeWineItem
} from "../models/menu"
import {
  allergenMembership,
  filterByAllergens,
  isFilterAvailable,
  listingFor
} from "../models/snapshot"
import { toggleFilter } from "../models/allergens"
import { firstSyncMessage } from "../models/syncStatus"

// Rows rendered per step; the whole listing is already on the device.
const PAGE_SIZE = 20
const NONE = []

const NORMALIZE = {
  [ITEM_KIND.FOOD]: (raw) => normalizeFoodItem(raw),
  [ITEM_KIND.WINE]: (raw) => normalizeWineItem(raw),
  [ITEM_KIND.BAR]: (raw, categoryName) => normalizeBarItem(raw, categoryName),
  [ITEM_KIND.SPIRIT]: (raw) => normalizeSpiritItem(raw)
}

// Browses the menu saved on the device; nothing here waits on the network.
export default function useMenuBrowser() {
  const { menu, sync } = useMenuData()
  const [selectedPeriodName, setSelectedPeriodName] = useState("")
  const [selectedCategoryName, setSelectedCategoryName] = useState("")
  const [chosenAllergenIds, setChosenAllergenIds] = useState(NONE)
  const [showAllergens, setShowAllergens] = useState(true)
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const mealPeriods = menu?.mealPeriods ?? NONE

  const selectedPeriod = useMemo(
    () => mealPeriods.find((period) => period.name === selectedPeriodName) ?? null,
    [mealPeriods, selectedPeriodName]
  )

  // Bar and Spirits carry their own category lists; everything else uses food_list.
  const categoriesFor = useCallback(
    (period) => {
      if (!period || !menu) return NONE
      if (period.name === MEAL_PERIOD.BAR) return menu.barCategories
      if (period.name === MEAL_PERIOD.SPIRITS) return menu.spiritCategories
      return period.categories
    },
    [menu]
  )

  const categories = useMemo(() => categoriesFor(selectedPeriod), [categoriesFor, selectedPeriod])

  // A fresh snapshot can drop the selected tab; fall back to the first one still there.
  useEffect(() => {
    if (!mealPeriods.length) return
    const period = mealPeriods.find((entry) => entry.name === selectedPeriodName) ?? mealPeriods[0]
    if (period.name !== selectedPeriodName) setSelectedPeriodName(period.name)
    const periodCategories = categoriesFor(period)
    if (!periodCategories.some((category) => category.name === selectedCategoryName)) {
      setSelectedCategoryName(periodCategories[0]?.name ?? "")
    }
  }, [mealPeriods, selectedPeriodName, selectedCategoryName, categoriesFor])

  const supportsAllergenFilter = Boolean(selectedPeriod?.hasAllergens)

  const allergenFilters = useMemo(
    () => ALLERGEN_FILTERS.filter((filter) => isFilterAvailable(menu, filter.id)),
    [menu]
  )

  // A refreshed copy without a diet group drops that diet rather than filtering on nothing.
  const selectedAllergenIds = useMemo(() => {
    const kept = chosenAllergenIds.filter((id) => isFilterAvailable(menu, id))
    return kept.length === chosenAllergenIds.length ? chosenAllergenIds : kept
  }, [menu, chosenAllergenIds])

  const listing = useMemo(
    () => (selectedPeriod ? listingFor(menu, selectedPeriod.name, selectedCategoryName) : null),
    [menu, selectedPeriod, selectedCategoryName]
  )

  const allItems = useMemo(() => {
    if (!listing) return NONE
    const isFiltering = supportsAllergenFilter && selectedAllergenIds.length > 0
    const raws = isFiltering
      ? filterByAllergens(listing.items, allergenMembership(menu, selectedAllergenIds), showAllergens)
      : listing.items
    const normalize = NORMALIZE[listing.kind] ?? NORMALIZE[ITEM_KIND.FOOD]
    return raws.map((raw) => normalize(raw, selectedCategoryName))
  }, [menu, listing, selectedCategoryName, supportsAllergenFilter, selectedAllergenIds, showAllergens])

  // A new selection starts from the first rows again; a background sync does not.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [selectedPeriodName, selectedCategoryName, selectedAllergenIds, showAllergens])

  const items = useMemo(() => allItems.slice(0, visibleCount), [allItems, visibleCount])
  const hasMore = visibleCount < allItems.length

  const loadMore = useCallback(() => {
    if (hasMore) setVisibleCount((count) => count + PAGE_SIZE)
  }, [hasMore])

  // Loading only until a first copy exists; an error only when that first sync failed.
  const isSettled = sync.phase === SYNC_PHASE.IDLE
  const hasFailedFirstSync = !menu && isSettled && Boolean(sync.lastError)
  const isUnreadable = !menu && isSettled && sync.hasData
  const isLoading = !menu && !hasFailedFirstSync && !isUnreadable
  const error = hasFailedFirstSync
    ? { message: firstSyncMessage(sync.lastError) }
    : isUnreadable
      ? { message: "The saved menu could not be read." }
      : null

  const selectPeriod = useCallback(
    (period) => {
      setSelectedPeriodName(period.name)
      // A meal period switch drops the allergen filter, matching the native app.
      setChosenAllergenIds(NONE)
      setShowAllergens(true)
      setSelectedCategoryName(categoriesFor(period)[0]?.name ?? "")
    },
    [categoriesFor]
  )

  const selectCategory = useCallback((category) => setSelectedCategoryName(category.name), [])

  const toggleAllergen = useCallback(
    (allergenId) => {
      const isSelected = selectedAllergenIds.includes(allergenId)
      setChosenAllergenIds(toggleFilter(selectedAllergenIds, allergenId))
      // Adding a filter always returns to the including side ("With Allergens", or a lone diet).
      if (!isSelected) setShowAllergens(true)
    },
    [selectedAllergenIds]
  )

  const categoryHeader = useMemo(() => {
    if (selectedPeriod?.name !== MEAL_PERIOD.SPIRITS || !items.length) return null
    const category = categories.find((entry) => entry.name === selectedCategoryName)
    return category?.description ? category : null
  }, [selectedPeriod, categories, selectedCategoryName, items.length])

  const emptyMessage = listing?.hasError
    ? "This category couldn't be loaded right now"
    : selectedAllergenIds.length
      ? "No items match this filter"
      : "No items in this category"

  return {
    mealPeriods,
    selectedPeriod,
    categories,
    selectedCategoryName,
    categoryHeader,
    items,
    isLoading,
    isLoadingMore: false,
    hasMore,
    error,
    emptyMessage,
    supportsAllergenFilter,
    allergenFilters,
    selectedAllergenIds,
    showAllergens,
    setShowAllergens,
    toggleAllergen,
    selectPeriod,
    selectCategory,
    loadMore
  }
}
